import { Typography, Button, Card, Box, Breadcrumbs, MenuItem, TextField } from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MapPin,
  Mail,
  Phone,
  Clock,
  Edit,
  Building2,
  Users,
  Cpu,
  House,
  Trash2,
  Power,
  ArrowLeft,
} from 'lucide-react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import { MODULE_NAME, PERMISSIONS } from '../../../constants'
import { useLanguage } from '../../../i18n'
import { hasPermission } from '../../../utils'
import { getBranchApi, deleteBranchApi, updateBranchStatusApi } from '../api'
import { branchStatusConfig, branchStatusOptions } from '../constant'

import { InfoRow, SectionHeader, StatusBadge } from '@/common/components'

const MetricCard = ({ label, value, sub, icon }) => (
  <div className="sensor-metric">
    <span className="sensor-metric__label flex gap-1 items-center">
      {icon} {label}
    </span>
    <span className="sensor-metric__value">{value}</span>
    {sub && <span className="sensor-metric__sub">{sub}</span>}
  </div>
)

export default function BranchDetail() {
  const { t } = useLanguage()
  const { uuid } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  // Branch-scoped permissions — hide update/delete controls the current
  // user is not allowed to use.
  const canUpdate = hasPermission(MODULE_NAME.BRANCH, PERMISSIONS.UPDATE)
  const canDelete = hasPermission(MODULE_NAME.BRANCH, PERMISSIONS.DELETE)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['branch-detail', uuid],
    queryFn: async () => {
      const res = await getBranchApi(uuid)
      return res?.data?.data || res?.data || res
    },
    enabled: Boolean(uuid),
  })

  const swalDefaults = {
    confirmButtonColor: '#3B82F6',
    cancelButtonColor: '#151f3b',
    background: 'var(--card-bg, #ffffff)',
    color: 'var(--text-primary, #212529)',
    customClass: {
      confirmButton: 'swal-btn-primary',
      cancelButton: 'swal-btn-secondary',
    },
    didOpen: () => {
      const container = document.querySelector('.swal2-container')
      if (container) container.style.zIndex = '2000'
    },
  }
  const extractError = (err) =>
    err?.response?.data?.status?.message ||
    err?.response?.data?.message ||
    err?.message ||
    t('Something went wrong')

  const statusMut = useMutation({
    mutationFn: ({ status }) => updateBranchStatusApi(uuid, status),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['branch-detail', uuid] })
      qc.invalidateQueries({ queryKey: ['branches-list'] })
      await Swal.fire({
        ...swalDefaults,
        title: t('Status updated'),
        icon: 'success',
        iconColor: '#198754',
        confirmButtonText: t('OK'),
      })
    },
    onError: async (err) => {
      await Swal.fire({
        ...swalDefaults,
        title: t('Status update failed'),
        text: extractError(err),
        icon: 'error',
        iconColor: '#DC3545',
        confirmButtonText: t('OK'),
      })
    },
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteBranchApi(uuid),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['branches-list'] })
      navigate('/branch-management')
      await Swal.fire({
        ...swalDefaults,
        title: t('Branch deleted'),
        text: t('The branch was removed.'),
        icon: 'success',
        iconColor: '#198754',
        confirmButtonText: t('OK'),
      })
    },
    onError: async (err) => {
      await Swal.fire({
        ...swalDefaults,
        title: t('Delete failed'),
        text: extractError(err),
        icon: 'error',
        iconColor: '#DC3545',
        confirmButtonText: t('OK'),
      })
    },
  })

  const handleRequestStatusChange = async (branchObj) => {
    const next = branchObj.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    const isActivating = branchObj.status !== 'ACTIVE'
    const result = await Swal.fire({
      ...swalDefaults,
      title: isActivating
        ? t('Activate {{name}}?', { name: branchObj.name })
        : t('Deactivate {{name}}?', { name: branchObj.name }),
      text: t('Status will be changed to {{status}} immediately.', { status: next }),
      icon: 'warning',
      iconColor: '#f7b84b',
      showCancelButton: true,
      confirmButtonText: isActivating ? t('Activate') : t('Deactivate'),
      cancelButtonText: t('Cancel'),
    })
    if (result.isConfirmed) {
      statusMut.mutate({ status: next })
    }
  }

  const handleRequestDelete = async (branchObj) => {
    const result = await Swal.fire({
      ...swalDefaults,
      title: t('Delete {{name}}?', { name: branchObj.name }),
      text: t(
        'This will permanently remove this branch and all of its associated facilities and devices. This action cannot be undone.'
      ),
      icon: 'warning',
      iconColor: '#DC3545',
      showCancelButton: true,
      confirmButtonText: t('Delete Branch'),
      cancelButtonText: t('Cancel'),
    })
    if (result.isConfirmed) {
      deleteMut.mutate()
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('Loading branch…')}
        </Typography>
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error" gutterBottom>
          {t('Failed to load branch')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error?.message || t('Branch not found')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={14} />}
          sx={{ mt: 2 }}
          onClick={() => navigate('/branch-management')}
        >
          {t('Back to branches')}
        </Button>
      </Box>
    )
  }

  const branch = data
  const statusKey = branch.status || 'INACTIVE'
  const statusMeta = branchStatusConfig[statusKey] || { labelKey: statusKey }

  const IDENTIFICATION_ROWS = [
    { label: t('Branch code'), value: branch.code || branch.branchCode || '—' },
    { label: t('Branch name'), value: branch.name || '—' },
    { label: t('Region'), value: branch.region || '—' },
    { label: t('City'), value: branch.city || '—' },
    { label: t('Address'), value: branch.address || '—' },
    { label: t('Timezone'), value: branch.timezone || '—' },
    {
      label: t('Status'),
      value: <StatusBadge value={statusKey} config={branchStatusConfig} dot />,
    },
    {
      label: t('Main branch'),
      value: branch.isMainBranch ? t('Yes') : t('No'),
    },
  ]

  const MANAGER_ROWS = [
    {
      label: t('Manager name'),
      icon: <Users size={14} />,
      value: branch.manager || branch.managerName || '—',
    },
    {
      label: t('Manager email'),
      icon: <Mail size={14} />,
      value: branch.managerEmail || '—',
    },
    {
      label: t('Manager phone'),
      icon: <Phone size={14} />,
      value: branch.managerPhone || '—',
    },
    {
      label: t('Working hours'),
      icon: <Clock size={14} />,
      value: branch.workingHours || '—',
    },
  ]

  const COUNTS = [
    {
      label: t('Facilities'),
      value: branch.facilities ?? 0,
      sub: t('Active sites'),
      icon: <House size={13} />,
    },
    {
      label: t('Devices'),
      value: branch.devices ?? 0,
      sub: branch.alarms ? t('{{count}} in alarm', { count: branch.alarms }) : t('No alarms'),
      icon: <Cpu size={13} />,
    },
    {
      label: t('Users'),
      value: branch.users ?? 0,
      sub: t('Across roles'),
      icon: <Users size={13} />,
    },
  ]

  return (
    <div className="sensor-page">
      {/* Top Bar */}
      <div className="sensor-topbar">
        <div>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Link to="/branch-management" underline="hover" color="inherit">
              {t('Branch Management')}
            </Link>
            <Typography variant="body2" color="text.primary">
              {branch.name || t('Branch')}
            </Typography>
          </Breadcrumbs>
          <div className="flex items-center gap-3 mb-1">
            <Typography variant="h5">{branch.name || '—'}</Typography>
            <StatusBadge value={statusKey} config={branchStatusConfig} dot>
              {statusMeta.labelKey ? t(statusMeta.labelKey) : statusKey}
            </StatusBadge>
            <StatusBadge color="blue">{branch.code || branch.uuid?.slice(0, 8) || '—'}</StatusBadge>
          </div>
          <Typography className="text-gray-400">
            {branch.region || '—'} · {branch.city || '—'} · {branch.timezone || '—'} ·{' '}
            {t('{{facilities}} facilities · {{devices}} devices', {
              facilities: branch.facilities ?? 0,
              devices: branch.devices ?? 0,
            })}
          </Typography>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {canUpdate && (
            <Button variant="outlined" startIcon={<Edit size={14} />}>
              {t('Edit')}
            </Button>
          )}
          {canUpdate && (
            <Button
              variant="outlined"
              startIcon={<Power size={14} />}
              onClick={() => handleRequestStatusChange(branch)}
            >
              {statusKey === 'ACTIVE' ? t('Deactivate') : t('Activate')}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Trash2 size={14} />}
              onClick={() => handleRequestDelete(branch)}
            >
              {t('Delete branch')}
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="sensor-body">
        {/* LEFT COLUMN */}
        <div className="flex flex-col gap-5">
          {/* Overview */}
          <Card className="px-4 py-3">
            <SectionHeader icon={Building2} label={t('Overview')} />
            <div className="sensor-metrics">
              {COUNTS.map((m) => (
                <MetricCard key={m.label} {...m} />
              ))}
            </div>
          </Card>

          {/* Location */}
          <Card className="p-5">
            <SectionHeader icon={MapPin} label={t('Location')} />
            <div className="sensor-location">
              <MapPin size={14} />
              <Typography variant="body1">{branch.address || '—'}</Typography>
            </div>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t('Public view-only URL')}
              </Typography>
              <Typography variant="body1" fontWeight={400}>
                {t('No-login QR page · disabled')}
              </Typography>
            </Box>
          </Card>

          {/* Facility list placeholder — pulls from branch.facilityList when backend exposes it */}
          <Card className="p-5">
            <SectionHeader
              icon={House}
              label={
                <>
                  {t('Facilities')}{' '}
                  <span className="section-header__count">· {branch.facilities ?? 0}</span>{' '}
                </>
              }
              action={
                <Button size="small" variant="outlined" className="sensor-btn-tiny">
                  {t('View all')}
                </Button>
              }
            />
            <Box className="flex flex-col gap-2">
              {(branch.facilityList && branch.facilityList.length > 0
                ? branch.facilityList
                : ['Cold Room A', 'Freezer Bay 02', 'Ambient Warehouse', 'Pharma Vault']
              )
                .slice(0, 4)
                .map((f, idx) => {
                  const name = typeof f === 'string' ? f : f?.name || `Facility ${idx + 1}`
                  return (
                    <Box
                      key={name + idx}
                      className="flex justify-between items-center"
                      sx={{
                        p: 1.5,
                        border: '1px solid var(--divider-color)',
                        borderRadius: 1,
                      }}
                    >
                      <Box className="flex items-center gap-2">
                        <House size={14} />
                        <Typography variant="body2" fontWeight={600}>
                          {name}
                        </Typography>
                      </Box>
                      <StatusBadge color="green" dot>
                        {t('Online')}
                      </StatusBadge>
                    </Box>
                  )
                })}
            </Box>
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col gap-5">
          {/* Identification */}
          <Card className="p-5">
            <SectionHeader icon={Building2} label={t('Identification')} />
            {IDENTIFICATION_ROWS.map((row) => (
              <InfoRow key={row.label} {...row} />
            ))}
          </Card>

          {/* Branch Manager */}
          <Card className="p-5">
            <SectionHeader icon={Users} label={t('Branch Manager')} />
            <div className="flex flex-col">
              {MANAGER_ROWS.map((row) => (
                <InfoRow key={row.label} {...row} />
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Inline status editor (optional direct select) */}
      <Card className="p-5" sx={{ mt: 3 }}>
        <SectionHeader icon={Power} label={t('Quick Status Update')} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <TextField
            select
            size="small"
            value={statusKey}
            onChange={(e) =>
              statusMut.mutate(
                { status: e.target.value },
                {
                  onSuccess: () => qc.invalidateQueries({ queryKey: ['branch-detail', uuid] }),
                }
              )
            }
            sx={{ minWidth: 220 }}
            disabled={statusMut.isPending}
          >
            {branchStatusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            {t('Changes are persisted immediately via the status endpoint.')}
          </Typography>
        </Box>
      </Card>
    </div>
  )
}
